/**
 * LearnLayout - Shared layout for public documentation pages
 * 
 * DEX Protocol: Premium Minimalism, Playfair Display, Graphics > Words
 * KAI Protocol: 700ms transitions, magnetic buttons, 100vw sections
 * ZIA Protocol: AIO-optimized structured data integration
 */

import React from "react";
import { Link, useLocation } from "react-router-dom";
import StructuredData from "../marketing/StructuredData";

const getOrigin = () => {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "https://eq.app";
};

// Navigation items for the Learn hub
const navItems = [
  { path: "/learn", label: "Hub", exact: true },
  { path: "/learn/compare/linkedin-recruiter", label: "vs LinkedIn" },
  { path: "/learn/zero-admin-recruiting", label: "Zero Admin" },
  { path: "/learn/how-ai-works", label: "Glass Box" },
  { path: "/learn/data-control", label: "Data Control" },
];

export default function LearnLayout({ 
  children, 
  structuredData,
  pageTitle = "Learn",
  pageDescription = "Intelligence Library"
}) {
  const location = useLocation();
  const origin = getOrigin();

  // Base structured data for all learn pages (AIO optimization)
  const baseStructuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "EQ.app",
    "applicationCategory": "Recruiting Software",
    "offers": {
      "@type": "Offer",
      "description": "AI-powered recruiting platform, LinkedIn alternative"
    },
    "featureList": [
      "Zero Admin recruiting",
      "AI candidate sourcing",
      "Full data ownership",
      "10M+ profile database"
    ]
  };

  const combinedData = structuredData 
    ? [baseStructuredData, ...structuredData] 
    : [baseStructuredData];

  return (
    <div className="min-h-screen bg-slate-50">
      <StructuredData data={combinedData} />
      
      {/* Header Navigation */}
      <header 
        className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200"
        style={{ transition: "all 700ms ease-out" }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center gap-3 group"
              style={{ transition: "transform 700ms ease-out" }}
            >
              <div className="w-8 h-8 bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>EQ</span>
              </div>
              <span 
                className="text-slate-900 font-semibold tracking-tight group-hover:text-[#5a20ff]"
                style={{ fontFamily: "'Playfair Display', serif", transition: "color 700ms ease-out" }}
              >
                Intelligence Library
              </span>
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const isActive = item.exact 
                  ? location.pathname === item.path 
                  : location.pathname.startsWith(item.path);
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all hover:scale-105 ${
                      isActive 
                        ? "bg-[#5a20ff] text-white" 
                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                    }`}
                    style={{ 
                      fontFamily: "'Playfair Display', serif",
                      transition: "all 700ms ease-out" 
                    }}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            {/* CTA */}
            <Link
              to="/app"
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-[#5a20ff] text-white text-sm font-semibold shadow-lg hover:bg-[#4a1ad6] hover:scale-105"
              style={{ 
                fontFamily: "'Playfair Display', serif",
                transition: "all 700ms ease-out" 
              }}
            >
              Try Free
            </Link>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      <div className="md:hidden sticky top-16 z-40 bg-white border-b border-slate-200 overflow-x-auto">
        <div className="flex items-center gap-2 px-4 py-3">
          {navItems.map((item) => {
            const isActive = item.exact 
              ? location.pathname === item.path 
              : location.pathname.startsWith(item.path);
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
                  isActive 
                    ? "bg-[#5a20ff] text-white" 
                    : "text-slate-600 bg-slate-100"
                }`}
                style={{ 
                  fontFamily: "'Playfair Display', serif",
                  transition: "all 700ms ease-out" 
                }}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <main 
        className="animate-fadeIn"
        style={{ 
          animation: "fadeIn 700ms ease-out",
        }}
      >
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16 mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>EQ</span>
                </div>
                <span 
                  className="text-xl font-semibold"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  EQ.app
                </span>
              </div>
              <p className="text-slate-400 text-sm max-w-md" style={{ fontFamily: "'Playfair Display', serif" }}>
                The recruiting intelligence platform that eliminates admin work. 
                Find candidates not on LinkedIn. Own your data. Scale your outreach.
              </p>
            </div>

            {/* Learn Links */}
            <div>
              <h4 
                className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Learn
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/learn/compare/linkedin-recruiter" className="text-slate-300 hover:text-white text-sm" style={{ transition: "color 700ms ease-out" }}>
                    EQ vs LinkedIn
                  </Link>
                </li>
                <li>
                  <Link to="/learn/how-ai-works" className="text-slate-300 hover:text-white text-sm" style={{ transition: "color 700ms ease-out" }}>
                    How AI Works
                  </Link>
                </li>
                <li>
                  <Link to="/learn/data-control" className="text-slate-300 hover:text-white text-sm" style={{ transition: "color 700ms ease-out" }}>
                    Data Control
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h4 
                className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Legal
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/legal/privacy" className="text-slate-300 hover:text-white text-sm" style={{ transition: "color 700ms ease-out" }}>
                    Privacy
                  </Link>
                </li>
                <li>
                  <Link to="/legal/terms" className="text-slate-300 hover:text-white text-sm" style={{ transition: "color 700ms ease-out" }}>
                    Terms
                  </Link>
                </li>
                <li>
                  <Link to="/legal/cookies" className="text-slate-300 hover:text-white text-sm" style={{ transition: "color 700ms ease-out" }}>
                    Cookies
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 mt-12 pt-8 text-center">
            <p className="text-slate-500 text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>
              Three Rockets. One Mission. Zero Admin.
            </p>
          </div>
        </div>
      </footer>

      {/* CSS for animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 700ms ease-out;
        }
      `}</style>
    </div>
  );
}
